import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { request } from "../utils/request";
import ChartCard from "./Dashboard/ChartCard";
import ChartRenderer from "./Dashboard/ChartRenderer";
import { enterpriseChartTheme } from "./Dashboard/chartTheme";

const chartTypes = [
  { value: "bar", label: "Bar Chart" },
  { value: "line", label: "Line Chart" },
  { value: "pie", label: "Pie Chart" },
];

const aggregationTypes = [
  { value: "count", label: "Count" },
  { value: "sum", label: "Sum" },
  { value: "avg", label: "Average" },
  { value: "min", label: "Minimum" },
  { value: "max", label: "Maximum" },
];

const emptyForm = {
  title: "",
  description: "",
  chartType: "bar",
  modelName: "",
  xField: "createdAt",
  yField: "_id",
  xLabel: "",
  yLabel: "",
  aggregationType: "count",
  filters: [],
  layout: { x: 0, y: 0, w: 6, h: 4, order: 1 },
  appearance: { primaryColor: "#3B82F6", secondaryColor: "#10B981" },
  cacheEnabled: false,
  cacheDuration: 300,
  isActive: true,
};

const CustomChartsPage = () => {
  const [charts, setCharts] = useState([]);
  const [models, setModels] = useState([]);
  const [fields, setFields] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [previewData, setPreviewData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const loadCharts = async () => {
    try {
      const res = await request("customcharts", { method: "GET" });
      setCharts(Array.isArray(res?.data) ? res.data : []);
    } catch (error) {
      console.error(error);
    }
  };

  const loadModels = async () => {
    try {
      const res = await request("customcharts/models", { method: "GET" });
      setModels(Array.isArray(res?.data) ? res.data : []);
    } catch (error) {
      console.error(error);
    }
  };

  const loadFields = async (modelName) => {
    if (!modelName) {
      setFields([]);
      return;
    }
    try {
      const res = await request(`customcharts/models/${encodeURIComponent(modelName)}/fields`, { method: "GET" });
      setFields(Array.isArray(res?.data) ? res.data : []);
    } catch (error) {
      console.error(error);
      setFields([]);
    }
  };

  useEffect(() => {
    loadCharts();
    loadModels();
  }, []);

  useEffect(() => {
    loadFields(form.modelName);
  }, [form.modelName]);

  const handleOpenCreate = () => {
    setForm(emptyForm);
    setPreviewData([]);
    setEditingId(null);
    setOpen(true);
  };

  const handleOpenEdit = (chart) => {
    setEditingId(chart._id);
    setForm({
      ...emptyForm,
      ...chart,
      modelName: chart.modelName || "",
      xField: chart.xField || "createdAt",
      yField: chart.yField || "_id",
      layout: chart.layout || emptyForm.layout,
      appearance: chart.appearance || emptyForm.appearance,
    });
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setPreviewData([]);
    setEditingId(null);
  };

  const previewChart = async () => {
    if (!form.modelName) return;
    setLoading(true);
    try {
      const res = await request("customcharts/preview", {
        method: "POST",
        body: {
          ...form,
          filters: Array.isArray(form.filters) ? form.filters : [],
        },
      });
      setPreviewData(Array.isArray(res?.data) ? res.data : []);
    } catch (error) {
      console.error(error);
      setPreviewData([]);
    } finally {
      setLoading(false);
    }
  };

  const saveChart = async () => {
    try {
      const payload = {
        ...form,
        filters: Array.isArray(form.filters) ? form.filters : [],
      };
      if (editingId) {
        await request(`customcharts/${editingId}`, { method: "PUT", body: payload });
      } else {
        await request("customcharts", { method: "POST", body: payload });
      }
      handleClose();
      loadCharts();
    } catch (error) {
      console.error(error);
    }
  };

  const deleteChart = async (id) => {
    try {
      await request(`customcharts/${id}`, { method: "DELETE" });
      loadCharts();
    } catch (error) {
      console.error(error);
    }
  };

  const renderChart = useMemo(() => {
    if (!previewData.length) {
      return (
        <Box
          sx={{
            minHeight: 260,
            border: "1px dashed rgba(148, 163, 184, 0.45)",
            borderRadius: 3,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: "rgba(248, 250, 252, 0.7)",
            color: "#64748b",
            px: 2,
            textAlign: "center",
          }}
        >
          Preview will appear here after you run the preview.
        </Box>
      );
    }

    return <ChartRenderer data={previewData} chartType={form.chartType || "bar"} xKey="name" yKey="value" colors={[form.appearance?.primaryColor || enterpriseChartTheme.colors[0], form.appearance?.secondaryColor || enterpriseChartTheme.colors[1]]} />;
  }, [form.chartType, form.appearance, previewData]);

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" fontWeight={700} sx={{ color: "#0f172a" }}>Custom Charts</Typography>
          <Typography color="text.secondary" sx={{ mt: 0.5 }}>Create reusable charts from your MongoDB collections with a live preview.</Typography>
        </Box>
        <Button variant="contained" onClick={handleOpenCreate} sx={{ borderRadius: 999, px: 2.2, py: 1 }}>Create Chart</Button>
      </Stack>

      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3, borderRadius: 3, border: "1px solid rgba(15, 23, 42, 0.08)", boxShadow: "0 10px 30px rgba(15, 23, 42, 0.08)", background: "linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)" }}>
            <Typography variant="h6" mb={2} sx={{ fontWeight: 700, color: "#0f172a" }}>Saved Charts</Typography>
            <Stack spacing={2}>
              {charts.map((chart) => (
                <ChartCard
                  key={chart._id}
                  title={chart.title || "Untitled chart"}
                  description={chart.description || "No description provided"}
                  meta={{ modelName: chart.modelName || "Collection", lastUpdated: chart.updatedAt ? new Date(chart.updatedAt).toLocaleDateString() : "Recently updated", subtitle: `${chart.chartType || "bar"} • ${chart.aggregationType || "count"}` }}
                  isActive={chart.isActive !== false}
                  onEdit={() => handleOpenEdit(chart)}
                  onDelete={() => deleteChart(chart._id)}
                  onRefresh={() => previewChart(chart)}
                >
                  <Stack spacing={1.25}>
                    <Stack direction="row" spacing={1} flexWrap="wrap">
                      <Chip label={chart.modelName} size="small" sx={{ borderRadius: 999, background: "rgba(37, 99, 235, 0.08)", color: "#2563eb" }} />
                      <Chip label={chart.chartType} size="small" sx={{ borderRadius: 999, background: "rgba(15, 23, 42, 0.06)", color: "#334155" }} />
                      <Chip label={chart.aggregationType} size="small" sx={{ borderRadius: 999, background: "rgba(16, 185, 129, 0.1)", color: "#047857" }} />
                    </Stack>
                    <Box sx={{ height: 220, mt: 0.5 }}>
                      <ChartRenderer data={chart.previewData || []} chartType={chart.chartType || "bar"} xKey="name" yKey="value" colors={[chart.appearance?.primaryColor || enterpriseChartTheme.colors[0], chart.appearance?.secondaryColor || enterpriseChartTheme.colors[1]]} />
                    </Box>
                  </Stack>
                </ChartCard>
              ))}
              {!charts.length && (
                <Box sx={{ border: "1px dashed rgba(148, 163, 184, 0.45)", borderRadius: 3, py: 5, textAlign: "center", background: "rgba(248, 250, 252, 0.7)" }}>
                  <Typography color="text.secondary">No charts saved yet. Create your first chart to start building your analytics workspace.</Typography>
                </Box>
              )}
            </Stack>
          </Paper>
        </Grid>

        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3, borderRadius: 3, border: "1px solid rgba(15, 23, 42, 0.08)", boxShadow: "0 10px 30px rgba(15, 23, 42, 0.08)", background: "linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)" }}>
            <Typography variant="h6" mb={1} sx={{ fontWeight: 700, color: "#0f172a" }}>Chart Builder</Typography>
            <Typography variant="body2" color="text.secondary">Preview your configuration before saving it.</Typography>
          </Paper>
        </Grid>
      </Grid>

      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>{editingId ? "Edit Chart" : "Create Chart"}</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField label="Title" fullWidth value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField label="Description" fullWidth value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Chart Type</InputLabel>
                <Select value={form.chartType} label="Chart Type" onChange={(e) => setForm({ ...form, chartType: e.target.value })}>
                  {chartTypes.map((item) => <MenuItem key={item.value} value={item.value}>{item.label}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Model</InputLabel>
                <Select value={form.modelName} label="Model" onChange={(e) => setForm({ ...form, modelName: e.target.value, xField: "createdAt", yField: "_id" })}>
                  {models.map((model) => <MenuItem key={model.modelName} value={model.modelName}>{model.name}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Aggregation</InputLabel>
                <Select value={form.aggregationType} label="Aggregation" onChange={(e) => setForm({ ...form, aggregationType: e.target.value })}>
                  {aggregationTypes.map((item) => <MenuItem key={item.value} value={item.value}>{item.label}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>X Axis Field</InputLabel>
                <Select value={form.xField} label="X Axis Field" onChange={(e) => setForm({ ...form, xField: e.target.value })}>
                  {fields.map((field) => <MenuItem key={field.name} value={field.name}>{field.name}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Y Axis Field</InputLabel>
                <Select value={form.yField} label="Y Axis Field" onChange={(e) => setForm({ ...form, yField: e.target.value })}>
                  {fields.map((field) => <MenuItem key={field.name} value={field.name}>{field.name}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField label="X Label" fullWidth value={form.xLabel} onChange={(e) => setForm({ ...form, xLabel: e.target.value })} />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField label="Y Label" fullWidth value={form.yLabel} onChange={(e) => setForm({ ...form, yLabel: e.target.value })} />
            </Grid>
            <Grid item xs={12}>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle2" mb={1}>Preview</Typography>
                <Stack direction="row" spacing={1} mb={2}>
                  <Button variant="outlined" onClick={previewChart} disabled={loading || !form.modelName}>Preview</Button>
                  <Button variant="contained" color="secondary" onClick={saveChart}>Save Chart</Button>
                </Stack>
                {renderChart}
              </Paper>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default CustomChartsPage;
